import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Observable } from 'rxjs';
import { EndpointDTO } from 'src/components/types';
import { QrCodeResponseDTO } from 'src/pages/login/types';
import { NamingMapping } from 'src/components/visionx/super-admin/tenants/types';

export type JWTTokens = {
  sessionToken?: string;
  userToken?: string;
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
};

export type Session = {
  jwtTokens?: JWTTokens;
};

export interface SessionContextProps {
  clearSession: () => void;
  hasNoSession: () => boolean;
  getJwtSession: () => JWTTokens | undefined;
  setJwtSession: (tokens: JWTTokens) => void;
  isJwt: () => boolean;
  preSignIn: (username: string, password: string) => Promise<SignInResponseDTO>;
  signIn: (
    username: string,
    password: string,
    totp?: string,
    sessionToken?: string,
    challenge?: string,
  ) => Promise<SignInResponseDTO>;
  signIWithNewPassword: (
    username: string,
    password: string,
    sessionToken: string,
    challenge?: string,
  ) => Promise<SignInResponseDTO>;
  forgotPassword: (username: string) => Promise<SignInResponseDTO>;
  getQrCode: (username: string) => Promise<QrCodeResponseDTO>;
  verifyTotp: (code: string) => Promise<unknown>;
  watchLogin: () => Observable<GuslUser | null>;
  getLoggedInUser: () => User;
  watchSystemReady: () => Observable<boolean>;
  watchSessionStatus: () => Observable<SessionStatus>;
  get: <Response>(url: string, controller?: AbortController) => Promise<AxiosResponse<Response>>;
  _delete: <Response>(
    url: string,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Response>>;
  post: <Request, Response>(
    url: string,
    body?: Request,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Response>>;
  upload: <Request, Response>(
    url: string,
    body?: Request,
    controller?: AbortController,
    config?: AxiosRequestConfig,
  ) => Promise<AxiosResponse<Response>>;
  put: <Request, Response>(
    url: string,
    body?: Request,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Response>>;
  sendRequest: <Request, Response>(
    endpoint: EndpointDTO,
    body?: Request,
    controller?: AbortController,
  ) => Promise<AxiosResponse<Response>>;
  signOut: () => Promise<SignOutResponseDTO>;
  setOriginalPath: (pathname: string) => void;
  getOriginalPath: () => string;
  hasPermission: (permission: string) => boolean;
  hasStrictPermission: (permission: string) => boolean;
  isProduction: () => boolean;
}

export interface JwtTokenSignInDTO {
  userToken?: string;
  sessionToken?: string;
  idToken?: string;
  refreshToken?: string;
  accessToken?: string;
}

export interface SignInRequestDTO {
  token?: string;
  username?: string;
  password?: string;
  totp?: string;
  msalUsername?: string;
  msalToken?: string;
  msalName?: string;
}

export interface SignInRequestWithNewPasswordDTO {
  username: string;
  password: string;
  session: string;
}

export interface PermissionsDTO {
  permissions: string[];
}

export interface SignInResponseDTO {
  id: string;
  username: string;
  userToken: string;
  sessionToken: string;
  session?: string;
  idToken: string;
  refreshToken: string;
  accessToken: string;
  avatar?: string;
  passwordChangeRequired?: boolean;
  permissions?: PermissionsDTO;
  homePage?: string;
  homepage?: string;
  properties?: {
    [key: string]: string | number;
  };
  totpCodeRequired: boolean;
  totpSetupRequired: boolean;
  confirmationStatus?: string;
  siemType: string;
  challenge?: string;
  applicationMode?: string;
  namingMapping?: NamingMapping;
  mttrDisplay?: string;
  threatHubDisplay?: string;
  getVisibilityEnabled?: boolean;
  wizFeatureEnabled?: boolean;
  splunkMTTXFeatureEnabled?: boolean;
  defenderFeatureEnabled?: boolean;
  sentinelOneFeatureEnabled?: boolean;
  sentinelMttxFeatureEnabled?: boolean;
  noPhishFeatureEnabled?: boolean;
  jiraIncidentsFeatureEnabled?: boolean;
  cisoTrainingFeature?: boolean;
  tenantShortName?: string;
  timeToIdle?: string;
}

export interface SignOutResponseDTO {
  success: boolean;
}

export interface User {
  id: string;
  email: string;
  avatar?: string;
  isSuperUser?: boolean | undefined;
}

export interface RoleDO {
  label: string;
  value: string;
}

export interface RolesDO {
  selections: RoleDO[];
}

export interface UserDO {
  id: string;
  username: string;
  roles: RolesDO;
  status: string;
  dateUpdated: string;
}

export interface GuslUser {
  googleUser?: unknown;
  user?: User;
  id: string;
  username: string;
  nickname?: string;
  userToken?: string;
  sessionToken?: string;
  avatar?: string;
  properties?: {
    [key: string]: string | number;
  };
  loggedIn?: boolean | undefined;
}

export enum SessionStatus {
  LOADING = 'LOADING',
  REQUESTING = 'REQUESTING',
  VALID = 'VALID',
  INVALID = 'INVALID',
}

export const incidentFilterDO: {
  [index: string]: { [index: string]: number };
} = {
  attackStage: {
    Compromise: 0,
    Delivery: 0,
    'Exploit & Install': 0,
    'N/A': 0,
    'Post Compromise': 0,
    'Recon & Suspicious': 0,
  },
  closedBy: {
    Analyst: 0,
    Customer: 0,
    Engineer: 0,
    'N/A': 0,
  },
  falsePositive: {
    'False Positive': 0,
    'N/A': 0,
    'Not Categorised': 0,
    'True Negative': 0,
    'True Positive': 0,
  },
  priority: {
    P1: 0,
    P2: 0,
    P3: 0,
    P4: 0,
  },
  resolution: {
    'N/A': 0,
    'No Response from Customer': 0,
    Resolved: 0,
  },
  status: {
    'Awaiting Approval': 0,
    Canceled: 0,
    Closed: 0,
    'Customer Requested Closure': 0,
    Resolved: 0,
    'Waiting Customer': 0,
    'Waiting Smarttech247': 0,
  },
  threatCategory: {
    'Authentication Issues': 0,
    'Malicious Activity': 0,
    'Malicious IP Access': 0,
    'Malicious Site Access': 0,
    'N/A': 0,
    Other: 0,
    'Possible Policy Violation': 0,
    'Security Group Changes': 0,
    'Suspicious Activity': 0,
    'Virus Detected': 0,
  },
  issueType: {
    'SOC Change Request': 0,
    'SOC Analyst Ticket': 0,
    'SOC Email Request': 0,
    'SOC Engineering Ticket': 0,
    'SOC Incident': 0,
    'SOC Non-Notify Issue': 0,
    'SOC Report': 0,
  },
};

export const noPhishFilterDO: { [index: string]: { [index: string]: number } } = {
  activityType: {
    'Business Email Compromise': 0,
    Clean: 0,
    'Malicious File Delivery': 0,
    'N/A': 0,
    Phishing: 0,
    'Phishing Simulation': 0,
    Spam: 0,
    'Standard Phishing': 0,
    'Targeted Credential Theft': 0,
  },
  falsePositive: {
    'False Positive': 0,
    'No Result': 0,
    'True Positive': 0,
  },
  maliciousElements: {
    Attachment: 0,
    'Body Text': 0,
    'Link/URL': 0,
    'N/A': 0,
  },
};

export interface AxiosConfigDO {
  common: {
    timeout: number;
    headers: {
      accept: string;
    };
  };
  json: {
    headers: {
      'Content-Type': string;
    };
  };
  formUrlEncoded: {
    headers: {
      'Content-Type': string;
    };
  };
  formData: {
    headers: {
      'Content-Type': string;
    };
  };
  authenticated: {
    headers: {
      'Content-Type': string;
    };
  };
}

export interface DecodedToken {
  sub: string;
  google_grups?: string[];
  email_verified: boolean;
  iss: string;
  'cognito:username': string;
  'custom:tenant_id': string;
  'custom:organisation_id': string;
  origin_jti: string;
  aud: string;
  event_id: string;
  token_use: string;
  auth_time: number;
  name: string;
  exp: number;
  iat: number;
  jti: string;
  email: string;
}
